import { getApiClient } from '../axios-config';

/**
 * Production Metrics API Service
 * Handles all API calls for the Production Metrics Dashboard.
 */
class ProductionMetricsAPI {
  /**
   * Get headcount data (monthly table + weekly chart) for a project.
   * @param {string} projectId
   * @param {string|null} referenceDate - ISO date string (optional, defaults to today)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { projectId, months, weeklyChart, projectStartDate, projectFinishDate, dataQualityWarnings }
   */
  static async getHeadcountData(projectId, referenceDate, token) {
    const apiClient = getApiClient(token);
    const params = { projectId };
    if (referenceDate) params.referenceDate = referenceDate;

    const response = await apiClient.get(
      '/api/analytics/production-metrics/headcount',
      { params }
    );
    return response.data.data;
  }

  /**
   * Get package-level planned hour contributions for a headcount chart week.
   * @param {string} projectId
   * @param {string} weekEnding - ISO date string for the selected chart point
   * @param {'shop'|'field'} type
   * @param {string} token - JWT authentication token
   */
  static async getHeadcountActivePackages(projectId, weekEnding, type, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      '/api/analytics/production-metrics/headcount/active-packages',
      { params: { projectId, weekEnding, type } }
    );
    return response.data.data;
  }

  /**
   * Get labor curve data (cumulative hours) for a project.
   * @param {string} projectId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>}
   */
  static async getLaborCurve(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      '/api/analytics/production-metrics/labor-curve',
      { params: { projectId } }
    );
    return response.data.data;
  }

  /**
   * Get KPI weekly trends (PTD LPF and SPI history) for a project.
   * @param {string} projectId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { projectId, weeks: [], goodDirection: 'below' | 'above' }
   */
  static async getKpiTrends(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      '/api/analytics/production-metrics/kpi-trends',
      { params: { projectId } }
    );
    return response.data.data;
  }

  /**
   * Recalculate planned headcount schedule for a project (admin only).
   * @param {string} projectId
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { weeksCalculated, projectStartDate, projectFinishDate }
   */
  static async recalculateHeadcount(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      '/api/analytics/production-metrics/recalculate-headcount',
      { projectId }
    );
    return response.data.data;
  }

  /**
   * Recompute package duration + planned_field_finish from DB schedule activities (admin only).
   * @param {string} projectId
   * @param {string} token
   * @returns {Promise<{ packagesProcessed: number, packagesUpdated: number, packagesSkipped: number, packagesMissingLink: number }>}
   */
  static async recalculatePlannedDatesFromSchedule(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      '/api/analytics/production-metrics/recalculate-planned-dates',
      { projectId }
    );
    return response.data.data;
  }

  /**
   * Get categorised work-package progress data for a project.
   * @param {string} projectId
   * @param {string} token
   * @returns {Promise<Object>} { projectId, categories, totalBudgetHrs, dataQualityWarnings }
   */
  static async getPackages(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      '/api/analytics/production-metrics/packages',
      { params: { projectId } }
    );
    return response.data.data;
  }

  /**
   * Get project-level data quality warnings for the dashboard header.
   * @param {string} projectId
   * @param {string} token
   * @returns {Promise<{ projectId: string, dataQualityWarnings: Array }>}
   */
  static async getDataQualityWarnings(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      '/api/analytics/production-metrics/data-quality-warnings',
      { params: { projectId } }
    );
    return response.data.data;
  }

  /**
   * Get typed project-level data quality checks.
   * @param {string} projectId
   * @param {string} token
   * @returns {Promise<{ projectId: string, generatedAt: string, checks: Object, summary: Object }>}
   */
  static async getDataQuality(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      '/api/analytics/production-metrics/data-quality',
      { params: { projectId } }
    );
    return response.data.data;
  }

  /**
   * Update field or shop percent complete for a single package.
   * @param {string} packageId
   * @param {{ pctType: string, percentComplete: number, source?: string, notes?: string }} body
   * @param {string} token
   */
  static async updatePercentComplete(packageId, body, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.patch(
      `/api/analytics/production-metrics/packages/${packageId}/percent-complete`,
      body
    );
    return response.data.data;
  }

  /**
   * Redistribute shop vs. field budget hours for a package.
   * @param {string} packageId
   * @param {{ newShopBudgetHrs: number, newFieldBudgetHrs: number, notes?: string }} body
   * @param {string} token
   */
  static async updateBudgetSplit(packageId, body, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.patch(
      `/api/analytics/production-metrics/packages/${packageId}/budget-split`,
      body
    );
    return response.data.data;
  }

  /**
   * Get KPI summary (LPF, SPI, trends, labor hours) for a project week.
   * @param {string} projectId
   * @param {string} weekEnding - ISO date of Sunday (e.g. '2026-03-15')
   * @param {string} token
   * @returns {Promise<Object>}
   */
  static async getKpiSummary(projectId, weekEnding, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      '/api/analytics/production-metrics/summary',
      { params: { projectId, weekEnding } }
    );
    return response.data.data;
  }

  /**
   * Super-admin only. Snapshot row count for confirmation dialog.
   * @param {string} projectId
   * @param {string} token
   * @returns {Promise<{ projectId: string, rowCount: number }>}
   */
  static async getSnapshotRowCount(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      '/api/analytics/production-metrics/snapshots/row-count',
      { params: { projectId } }
    );
    return response.data.data;
  }

  /**
   * Super-admin only. Full snapshot regeneration for a project.
   * @param {string} projectId
   * @param {string} token
   * @returns {Promise<Object>} summary from API data payload
   */
  static async regenerateSnapshots(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      '/api/analytics/production-metrics/regenerate-snapshots',
      { projectId }
    );
    return response.data.data;
  }
}

export default ProductionMetricsAPI;
